import { Menu, Search } from 'lucide-react';

import { useOperator, operatorInitials } from '@/features/auth/operator-context';
import { ROLE_LABELS } from '@/lib/permissions';

import { EnvironmentControl } from './EnvironmentSwitcher';
import { QueueHealthPill } from './QueueHealthPill';
import { ThemeToggle } from './ThemeToggle';

interface TopBarProps {
  onOpenDrawer: () => void;
  onOpenPalette: () => void;
}

export function TopBar({ onOpenDrawer, onOpenPalette }: TopBarProps) {
  const { operator } = useOperator();

  return (
    <header
      className="border-bd sticky top-0 z-60 flex-none border-b backdrop-blur-[12px]"
      style={{ background: 'var(--chrome-bg)' }}
    >
      {/* mt-[3px] clears the fixed environment hairline. */}
      <div className="mt-[3px] flex h-14 items-center gap-2.5 px-3.5">
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="Open navigation"
          className="border-bd bg-elev text-tx grid size-11 flex-none place-items-center rounded-lg border md:hidden"
        >
          <Menu size={17} aria-hidden />
        </button>

        <div className="mr-1 flex flex-none items-baseline gap-2">
          <span
            className="font-head text-tx text-[21px] font-bold uppercase"
            style={{ letterSpacing: '0.06em' }}
          >
            Mesh
          </span>
          <span
            className="font-head text-tx3 hidden text-[14px] font-medium uppercase sm:inline"
            style={{ letterSpacing: '0.16em' }}
          >
            Admin
          </span>
        </div>

        {/* Desktop: a wide search affordance, since ⌘K is the primary nav route. */}
        <button
          type="button"
          onClick={onOpenPalette}
          className="border-bd bg-card hover:border-bd-s hover:bg-elev hidden h-9 max-w-[520px] flex-1 items-center gap-2.5 rounded-lg border pr-2.5 pl-3 text-left md:flex"
        >
          <Search size={13} className="text-tx3 flex-none" aria-hidden />
          <span className="text-tx3 flex-1 truncate text-[13px]">
            Search leagues, users, squads, ObjectIds, invite codes…
          </span>
          <kbd className="border-bd-s bg-high text-tx2 flex-none rounded border px-1.5 py-0.5 font-mono text-[10px]">
            ⌘K
          </kbd>
        </button>

        <div className="flex-1 md:hidden" />

        <button
          type="button"
          onClick={onOpenPalette}
          aria-label="Search"
          className="border-bd bg-elev text-tx grid size-11 flex-none place-items-center rounded-lg border md:hidden"
        >
          <Search size={16} aria-hidden />
        </button>

        <QueueHealthPill compact />
        <div className="hidden md:contents">
          <ThemeToggle />
        </div>
        <EnvironmentControl />

        <div className="border-bd ml-0.5 hidden flex-none items-center gap-2.5 border-l pl-2.5 md:flex">
          <div
            aria-hidden
            className="font-head text-on-brand grid size-[30px] place-items-center rounded-full text-[13px] font-semibold"
            style={{ background: 'var(--brand)', letterSpacing: '0.04em' }}
          >
            {operatorInitials(operator.name)}
          </div>
          <div className="flex flex-col leading-[1.15]">
            <span className="text-tx text-xs font-medium">{operator.name}</span>
            <span className="text-tx3 font-mono text-[9.5px] tracking-[0.04em] uppercase">
              {ROLE_LABELS[operator.role]}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
}
